/**
 * Onboarding Trigger Component
 *
 * Button to start or restart the onboarding tour.
 * Includes auto-trigger for first-time users.
 *
 * @module client/src/components/Onboarding/OnboardingTrigger
 */

import { useEffect } from 'react';
import { useOnboarding } from './OnboardingContext';
import { HelpCircle, PlayCircle, RotateCcw } from 'lucide-react';

interface OnboardingTriggerProps {
  autoStartForNewUsers?: boolean;
  className?: string;
  variant?: 'button' | 'icon' | 'link';
  showProgress?: boolean;
}

export function OnboardingTrigger({
  autoStartForNewUsers = true,
  className = '',
  variant = 'button',
  showProgress = false,
}: OnboardingTriggerProps) {
  const {
    state,
    progress,
    startOnboarding,
    resetOnboarding,
  } = useOnboarding();

  // Auto-start for new users
  useEffect(() => {
    if (autoStartForNewUsers && !state.hasCompletedOnboarding && !state.isActive) {
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        startOnboarding();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [autoStartForNewUsers, state.hasCompletedOnboarding, state.isActive, startOnboarding]);

  const handleClick = () => {
    if (state.hasCompletedOnboarding) {
      resetOnboarding();
      startOnboarding();
    } else {
      startOnboarding();
    }
  };

  if (state.isActive) {
    return null;
  }

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${className}`}
        aria-label={state.hasCompletedOnboarding ? 'Restart tour' : 'Start tour'}
        title={state.hasCompletedOnboarding ? 'Restart tour' : 'Start tour'}
      >
        {state.hasCompletedOnboarding ? (
          <RotateCcw className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        ) : (
          <HelpCircle className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        )}
      </button>
    );
  }

  if (variant === 'link') {
    return (
      <button
        onClick={handleClick}
        className={`text-coral hover:text-coral/80 underline text-sm transition-colors ${className}`}
        aria-label={state.hasCompletedOnboarding ? 'Restart tour' : 'Start tour'}
      >
        {state.hasCompletedOnboarding ? 'Retake tour' : 'Take a tour'}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center space-x-2 px-4 py-2 bg-coral hover:bg-coral/90 text-white rounded-lg transition-colors ${className}`}
      aria-label={state.hasCompletedOnboarding ? 'Restart tour' : 'Start tour'}
    >
      {state.hasCompletedOnboarding ? (
        <>
          <RotateCcw className="w-4 h-4" />
          <span>Retake Tour</span>
        </>
      ) : (
        <>
          <PlayCircle className="w-4 h-4" />
          <span>Start Tour</span>
        </>
      )}
      {showProgress && state.hasCompletedOnboarding && (
        <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
          {progress}% completed
        </span>
      )}
    </button>
  );
}

export default OnboardingTrigger;
